import { Box, ButtonBase, Tooltip, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { Check } from 'lucide-react';
import { useIntl } from 'react-intl';
import {
    COURSE_STAGE_VALUES,
    courseStageLabelId,
    type CourseStage,
} from '../model';
import { COURSE_STAGE_ICONS } from './courseStageIcons';

/** Mark and name sizes: `row` for a list row, `large` for the patient header. */
type StripSize = 'row' | 'large';

type Metrics = {
    dot: number;
    /** A done stage's circle, which carries a check. */
    doneDot: number;
    doneIcon: number;
    /** The current stage's circle, which carries the stage's glyph. */
    currentDot: number;
    icon: number;
    track: number;
    labelSize: number;
};

const METRICS: Record<StripSize, Metrics> = {
    row: {
        dot: 12,
        doneDot: 16,
        doneIcon: 10,
        currentDot: 22,
        icon: 12,
        track: 22,
        labelSize: 11,
    },
    large: {
        dot: 14,
        doneDot: 20,
        doneIcon: 13,
        currentDot: 30,
        icon: 16,
        track: 30,
        labelSize: 13,
    },
};

type StageState = 'current' | 'done' | 'todo';

const labelColor = (theme: Theme, state: StageState, isDone: boolean) => {
    if (state === 'current') {
        return theme.palette.text.primary;
    }
    return isDone
        ? theme.palette.text.secondary
        : theme.palette.action.disabled;
};

type CourseStageStripProps = {
    stage: CourseStage;
    done: readonly CourseStage[];
    size?: StripSize;
    /**
     * Whether each stage is named beneath its mark. Without names the strip
     * is marks and the current stage's glyph only, for where the stage is
     * named elsewhere on screen; it still reads out in full to assistive
     * technology and in its tooltip.
     */
    labels?: boolean;
    /**
     * Marks a stage done or undone. Omitted where the strip is a read-only
     * summary (the list rows); supplied in the panel, where the strip is the
     * checklist the user ticks off.
     */
    onToggleDone?: (stage: CourseStage) => void;
    disabled?: boolean;
};

/**
 * Every course stage, always all of them, in course order: checked once the
 * user has marked that stage done, the stage the course is on drawn larger
 * and carrying its glyph, the rest open.
 *
 * Not a progress bar. A course takes the stages it needs — contouring alone
 * is a whole course — so nothing is inferred from position: a stage is done
 * only when someone marked it done, a stage passed over stays open, and the
 * track between the marks never fills. Done and current are separate facts
 * and the strip never conflates them: the stage being worked is not drawn as
 * finished until someone says it is.
 *
 * One accent colour for every stage rather than each stage's own, because
 * the strip answers "what has this course finished", not "which stage is
 * this" — the stage's own colour identifies it in the stage picker and the
 * group header.
 */
export default function CourseStageStrip({
    stage,
    done,
    size = 'row',
    labels = true,
    onToggleDone,
    disabled = false,
}: CourseStageStripProps) {
    const intl = useIntl();
    const theme = useTheme();
    const metrics = METRICS[size];
    const accent = theme.palette.primary.main;

    const label = (value: CourseStage) =>
        intl.formatMessage({ id: courseStageLabelId(value) });

    const summary = intl.formatMessage(
        { id: 'patientList.stage.stripSummary' },
        {
            stage: label(stage),
            done: done.length,
            total: COURSE_STAGE_VALUES.length,
        }
    );

    const mark = (segment: CourseStage, state: StageState, isDone: boolean) => {
        if (state === 'current') {
            const Icon = COURSE_STAGE_ICONS[segment];
            return (
                <Box
                    component="span"
                    className="course-stage-mark"
                    data-stage={segment}
                    data-state={state}
                    sx={{
                        position: 'relative',
                        width: metrics.currentDot,
                        height: metrics.currentDot,
                        boxSizing: 'border-box',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // Filled once done; a ring while the course is on it.
                        border: `2px solid ${accent}`,
                        backgroundColor: isDone
                            ? accent
                            : theme.palette.background.default,
                        color: isDone ? theme.palette.common.white : accent,
                    }}
                >
                    <Icon size={metrics.icon} aria-hidden />
                </Box>
            );
        }
        if (isDone) {
            return (
                <Box
                    component="span"
                    className="course-stage-mark"
                    data-stage={segment}
                    data-state={state}
                    sx={{
                        position: 'relative',
                        width: metrics.doneDot,
                        height: metrics.doneDot,
                        boxSizing: 'border-box',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${accent}`,
                        backgroundColor: accent,
                        color: theme.palette.common.white,
                    }}
                >
                    <Check
                        size={metrics.doneIcon}
                        strokeWidth={3}
                        aria-hidden
                    />
                </Box>
            );
        }
        return (
            <Box
                component="span"
                className="course-stage-mark"
                data-stage={segment}
                data-state={state}
                sx={{
                    position: 'relative',
                    width: metrics.dot,
                    height: metrics.dot,
                    boxSizing: 'border-box',
                    borderRadius: '50%',
                    border: `2px solid ${alpha(theme.palette.text.secondary, 0.4)}`,
                    backgroundColor: 'transparent',
                }}
            />
        );
    };

    const cells = COURSE_STAGE_VALUES.map((segment, index) => {
        const isDone = done.includes(segment);
        const state: StageState =
            segment === stage ? 'current' : isDone ? 'done' : 'todo';

        const body = (
            <>
                <Box
                    component="span"
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: metrics.track,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {/* One neutral track through every mark. It groups the
                        stages and never fills: a filled run would claim the
                        stages between two marks were passed through. */}
                    {index > 0 && (
                        <Box
                            component="span"
                            sx={{
                                position: 'absolute',
                                left: 0,
                                width: '50%',
                                top: metrics.track / 2 - 1,
                                height: 2,
                                bgcolor: 'divider',
                            }}
                        />
                    )}
                    {index < COURSE_STAGE_VALUES.length - 1 && (
                        <Box
                            component="span"
                            sx={{
                                position: 'absolute',
                                right: 0,
                                width: '50%',
                                top: metrics.track / 2 - 1,
                                height: 2,
                                bgcolor: 'divider',
                            }}
                        />
                    )}
                    {mark(segment, state, isDone)}
                </Box>
                {labels && (
                    <Box
                        component="span"
                        sx={{
                            fontSize: metrics.labelSize,
                            fontWeight: state === 'current' ? 600 : 400,
                            color: labelColor(theme, state, isDone),
                            // Stage names wrap rather than run into the
                            // neighbouring stage when the cell is narrow.
                            textAlign: 'center',
                            lineHeight: 1.3,
                            px: 0.25,
                        }}
                    >
                        {label(segment)}
                    </Box>
                )}
            </>
        );

        const cellSx = {
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '5px',
        } as const;

        if (onToggleDone === undefined) {
            return (
                <Box key={segment} component="span" sx={cellSx}>
                    {body}
                </Box>
            );
        }

        const toggleLabel = intl.formatMessage(
            { id: 'patientList.stage.toggleDone' },
            { stage: label(segment) }
        );
        return (
            <Tooltip key={segment} title={toggleLabel}>
                {/* The whole column is the hit area, the name included; the
                    mark inside carries the paint. */}
                <ButtonBase
                    disabled={disabled}
                    aria-pressed={isDone}
                    aria-label={toggleLabel}
                    onClick={() => onToggleDone(segment)}
                    sx={{
                        ...cellSx,
                        // ButtonBase centres its content; the marks must sit on
                        // one line whether a name wraps or not.
                        justifyContent: 'flex-start',
                        borderRadius: 1,
                        py: 0.25,
                        '&:hover .course-stage-mark': {
                            borderColor: theme.palette.primary.light,
                        },
                    }}
                >
                    {body}
                </ButtonBase>
            </Tooltip>
        );
    });

    const stripSx = { display: 'flex', minWidth: 0, width: '100%' } as const;

    if (onToggleDone !== undefined) {
        return (
            <Box
                component="span"
                role="group"
                aria-label={summary}
                sx={stripSx}
            >
                {cells}
            </Box>
        );
    }

    return (
        <Tooltip title={summary}>
            <Box component="span" role="img" aria-label={summary} sx={stripSx}>
                {cells}
            </Box>
        </Tooltip>
    );
}
